'use client';

import clsx from 'clsx';
import Image from 'next/image';

import { useHandleQuickLink, type UrlTriggerEvent } from '../../hooks/UseNavigation';
import type { NavQuickLinkMedia } from '../NavQuickLinkItem';

import styles from './NavQuickLinkStoriesItem.module.scss';

export interface NavQuickLinkStoriesItemProps {
  /** System ID */
  sys?: { id: string };
  /** Background color for the wrapper */
  backgroundColor?: string | null;
  /** Whether to disable scroll to top on navigation */
  disableNavigationScrollToTop?: boolean | null;
  /** Whether this item is the active swiper slide */
  isSwiperActive?: boolean;
  /** Media asset (image) */
  media?: NavQuickLinkMedia | null;
  /** Top label text (category/tag) */
  topLabelText?: string | null;
  /** Navigation URL */
  url?: string | null;
  /** URL target (_self, _blank) */
  urlTarget?: string | null;
  /** Display text */
  urlText?: string | null;
  /** URL trigger event (opens modal instead of navigating) */
  urlTrigger?: UrlTriggerEvent | null;
  /** Optional className */
  className?: string;
  /** Test ID */
  'data-testid'?: string;
}

/**
 * NavQuickLinkStoriesItem - Story card navigation item
 *
 * A card-style link with image, top label, and title.
 * Used in story/article carousels.
 *
 * Features:
 * - Image with hover effect
 * - Top label for category/tag
 * - Active state with scale and shadow
 * - Used within Swiper carousel
 */
export function NavQuickLinkStoriesItem({
  backgroundColor,
  className,
  disableNavigationScrollToTop,
  isSwiperActive = false,
  media,
  topLabelText,
  url,
  urlTarget,
  urlText,
  urlTrigger,
  'data-testid': testId,
}: NavQuickLinkStoriesItemProps) {
  const { handleQuickLink } = useHandleQuickLink(
    url ?? '/',
    urlTarget ?? '_self',
    urlTrigger,
    disableNavigationScrollToTop
  );

  return (
    <button
      className={clsx(styles.wrapper, isSwiperActive && styles.active, className)}
      onClick={handleQuickLink}
      data-testid={testId ?? 'NavQuickLinkStoriesItemWrapper'}
      type="button"
    >
      {/* Top label/tag */}
      {topLabelText && (
        <span
          className={styles.topLabel}
          style={backgroundColor ? { backgroundColor } : undefined}
          data-testid="NavQuickLinkStoriesItemLabel"
        >
          {topLabelText}
        </span>
      )}

      {/* Image */}
      {media?.url && (
        <span className={styles.imgBox} data-testid="NavQuickLinkStoriesItemNavQuickImgBox">
          <Image
            src={media.url}
            alt={media.title ?? ''}
            fill
            sizes="(max-width: 768px) 160px, 244px"
            className={styles.img}
            aria-hidden
            data-testid="NavQuickLinkStoriesItemNavQuickImg"
          />
        </span>
      )}

      {/* Title */}
      <span className={styles.label} data-testid="NavQuickLinkStoriesItemLabel">
        {urlText}
      </span>
    </button>
  );
}

NavQuickLinkStoriesItem.displayName = 'NavQuickLinkStoriesItem';
